import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export interface ActionMenuItem {
  label: React.ReactNode;
  onClick: () => void;
  icon?: React.ReactNode;
  /** Color for destructive/important items (maps to theme palette). */
  color?: 'inherit' | 'primary' | 'error' | 'warning' | 'success' | 'info';
  disabled?: boolean;
  /** Render a divider above this item. */
  divider?: boolean;
  /** Hide the item entirely (e.g. permission-gated). */
  hidden?: boolean;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Tooltip/aria-label for the trigger. */
  label?: string;
  /** Trigger icon override. */
  icon?: React.ReactNode;
  size?: 'small' | 'medium';
}

/**
 * Compact overflow menu for grouping secondary/row actions behind a single
 * kebab trigger — keeps action columns from overflowing on mobile while keeping
 * everything reachable and touch-friendly.
 *
 * Permission/visibility is the caller's job via `item.hidden`.
 */
const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  label = 'عملیات بیشتر',
  icon,
  size = 'small',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const visibleItems = items.filter((i) => !i.hidden);
  if (visibleItems.length === 0) return null;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          aria-label={label}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleOpen}
          size={size}
        >
          {icon ?? <MoreVertIcon fontSize={size === 'small' ? 'small' : 'medium'} />}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        {visibleItems.map((item, idx) => {
          const node = (
            <MenuItem
              key={idx}
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
                item.onClick();
              }}
              sx={
                item.color && item.color !== 'inherit'
                  ? { color: `${item.color}.main`, '& .MuiListItemIcon-root': { color: `${item.color}.main` } }
                  : undefined
              }
            >
              {item.icon && <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>}
              <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                {item.label}
              </ListItemText>
            </MenuItem>
          );
          return item.divider ? (
            <React.Fragment key={idx}>
              <Divider />
              {node}
            </React.Fragment>
          ) : (
            node
          );
        })}
      </Menu>
    </>
  );
};

export default ActionMenu;
